import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  background: #fff;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const Title = styled.h3`
  font-size: 24px;
  color: #2c5aa0;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.h4`
  font-size: 18px;
  color: #444;
  margin: 25px 0 15px 0;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 10px;
`;

const UploadForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 30px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #555;
`;

const Input = styled.input`
  padding: 10px 14px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2c5aa0;
  }
`;

const FileUploadArea = styled.div<{ isDragOver: boolean }>`
  border: 2px dashed ${props => props.isDragOver ? '#2c5aa0' : '#dee2e6'};
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  background: ${props => props.isDragOver ? '#e8f0fe' : '#fff'};
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: #2c5aa0;
    background: #f8f9ff;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
`;

const BannerCard = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  }
`;

const BannerImage = styled.div<{ src: string }>`
  height: 140px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
`;

const BannerInfo = styled.div`
  padding: 15px;
`;

const BannerTitle = styled.div`
  font-weight: 600;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BannerMeta = styled.div`
  font-size: 12px;
  color: #888;
  margin-bottom: 10px;
`;



const Button = styled.button<{ variant?: 'primary' | 'danger' | 'secondary' }>`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  
  background: ${props =>
		props.variant === 'danger' ? '#dc3545' :
			props.variant === 'secondary' ? '#6c757d' :
				'#2c5aa0'};
  color: white;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' }>`
  padding: 10px 15px;
  border-radius: 6px;
  margin-top: 10px;
  background: ${props => props.type === 'success' ? '#d4edda' : '#f8d7da'};
  color: ${props => props.type === 'success' ? '#155724' : '#721c24'};
  font-size: 14px;
`;

interface Banner {
	id: string;
	title?: string;
	description?: string;
	fileUrl: string;
	uploadDate: string;
}

const AdminBannerManager: React.FC = () => {
	const [banners, setBanners] = useState<Banner[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isUploading, setIsUploading] = useState(false);

	// Upload Form State
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [isDragOver, setIsDragOver] = useState(false);
	const [status, setStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);

	const backendUrl = process.env.REACT_APP_API_URL || 'https://ijbc-backend-1089361061119.us-west1.run.app';
	const apiKey = localStorage.getItem('adminToken') || '';

	const fetchBanners = useCallback(async () => {
		try {
			const response = await fetch(`${backendUrl}/api/banners`);
			const data = await response.json();
			if (data.success) {
				setBanners(data.banners);
			}
		} catch (error) {
			console.error('Failed to fetch banners:', error);
		} finally {
			setIsLoading(false);
		}
	}, [backendUrl]);

	useEffect(() => {
		fetchBanners();
	}, [fetchBanners]);

	const handleDelete = async (id: string) => {
		if (!window.confirm('정말로 이 배너를 삭제하시겠습니까?')) return;

		try {
			const response = await fetch(`${backendUrl}/api/banners/${id}`, {
				method: 'DELETE',
				headers: {
					'X-API-Key': apiKey,
				},
			});

			const data = await response.json();
			if (data.success) {
				setBanners(prev => prev.filter(b => b.id !== id));
				setStatus({ type: 'success', message: '배너가 삭제되었습니다.' });
			} else {
				setStatus({ type: 'error', message: '삭제 실패: ' + data.error });
			}
		} catch (error) {
			setStatus({ type: 'error', message: '서버 오류가 발생했습니다.' });
		}
	};

	const handleFileSelect = (file: File) => {
		if (!file.type.startsWith('image/')) {
			setStatus({ type: 'error', message: '이미지 파일만 업로드 가능합니다.' });
			return;
		}
		setSelectedFile(file);
		setStatus(null);
	};

	const handleUpload = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!selectedFile) return;

		setIsUploading(true);
		setStatus(null);

		try {
			const formData = new FormData();
			formData.append('file', selectedFile);
			formData.append('title', title);
			formData.append('description', description);

			const response = await fetch(`${backendUrl}/api/upload-banner`, {
				method: 'POST',
				headers: {
					'X-API-Key': apiKey,
				},
				body: formData,
			});

			const data = await response.json();

			if (data.success) {
				setStatus({ type: 'success', message: '배너가 성공적으로 업로드되었습니다.' });
				setTitle('');
				setDescription('');
				setSelectedFile(null);
				fetchBanners(); // Refresh list
			} else {
				setStatus({ type: 'error', message: '업로드 실패: ' + data.error });
			}
		} catch (error) {
			setStatus({ type: 'error', message: '서버 오류가 발생했습니다.' });
		} finally {
			setIsUploading(false);
		}
	};

	return (
		<Container>
			<Title>🖼️ 배너 관리</Title>

			<SectionTitle>새 배너 업로드</SectionTitle>
			<UploadForm onSubmit={handleUpload}>
				<FormGroup>
					<Label>제목 (선택)</Label>
					<Input
						value={title}
						onChange={e => setTitle(e.target.value)}
						placeholder="배너 제목"
					/>
				</FormGroup>
				<FormGroup>
					<Label>설명 (선택)</Label>
					<Input
						value={description}
						onChange={e => setDescription(e.target.value)}
						placeholder="배너 설명"
					/>
				</FormGroup>

				<FileUploadArea
					isDragOver={isDragOver}
					onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
					onDragLeave={e => { e.preventDefault(); setIsDragOver(false); }}
					onDrop={e => {
						e.preventDefault();
						setIsDragOver(false);
						if (e.dataTransfer.files[0]) handleFileSelect(e.dataTransfer.files[0]);
					}}
					onClick={() => document.getElementById('banner-upload-input')?.click()}
				>
					<input
						id="banner-upload-input"
						type="file"
						accept="image/*"
						style={{ display: 'none' }}
						onChange={e => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
					/>
					{selectedFile ? (
						<div>
							<p>✅ {selectedFile.name}</p>
							<p style={{ fontSize: '12px', color: '#666' }}>
								{(selectedFile.size / 1024 / 1024).toFixed(2)} MB
							</p>
						</div>
					) : (
						<p>클릭하거나 이미지를 드래그하여 업로드하세요</p>
					)}
				</FileUploadArea>

				<Button type="submit" disabled={!selectedFile || isUploading}>
					{isUploading ? '업로드 중...' : '배너 등록하기'}
				</Button>

				{status && (
					<StatusMessage type={status.type}>
						{status.message}
					</StatusMessage>
				)}
			</UploadForm>

			<SectionTitle>등록된 배너 목록 ({banners.length})</SectionTitle>
			{isLoading ? (
				<p>로딩 중...</p>
			) : banners.length === 0 ? (
				<p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>
					등록된 배너가 없습니다.
				</p>
			) : (
				<Grid>
					{banners.map(banner => (
						<BannerCard key={banner.id}>
							<BannerImage src={banner.fileUrl} />
							<BannerInfo>
								<BannerTitle>{banner.title || '제목 없음'}</BannerTitle>
								<BannerMeta>
									{new Date(banner.uploadDate).toLocaleDateString()}
								</BannerMeta>
								<Button
									variant="danger"
									onClick={() => handleDelete(banner.id)}
									style={{ width: '100%' }}
								>
									삭제
								</Button>
							</BannerInfo>
						</BannerCard>
					))}
				</Grid>
			)}
		</Container>
	);
};

export default AdminBannerManager;
